/**
 * Error Logger Utility
 * Captures and logs errors to the backend for monitoring and debugging
 */

import supabase from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Queue for errors that occur before authentication
let pendingErrorQueue = [];
let isProcessingQueue = false;

/**
 * Get the current page/tab name from URL or state
 */
const getCurrentPage = () => {
  const path = window.location.pathname;
  const hash = window.location.hash;

  // Try to extract meaningful page name
  if (hash) return hash.replace('#', '');
  if (path === '/') return 'Home';

  return path.split('/').filter(Boolean).pop() || 'Unknown';
};

/**
 * Send error to backend
 */
const sendErrorToBackend = async (payload, token) => {
  try {
    const response = await fetch(
      `${API_URL}/api/error-logs`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    );

    if (response.ok) {
      console.log('\u2705 Error logged successfully');
      return true;
    } else {
      console.warn('\u26a0\ufe0f Failed to log error to backend:', response.status);
      return false;
    }
  } catch (e) {
    console.error('\u274c Failed to send error to backend:', e);
    return false;
  }
};

/**
 * Process queued errors once user is authenticated
 */
const processErrorQueue = async (token) => {
  if (isProcessingQueue || pendingErrorQueue.length === 0) return;
  isProcessingQueue = true;

  const errorsToProcess = [...pendingErrorQueue];
  pendingErrorQueue = [];

  for (const errorData of errorsToProcess) {
    await sendErrorToBackend(errorData, token);
  }

  // Also process any stored errors from localStorage
  try {
    const stored = JSON.parse(localStorage.getItem('pendingErrors') || '[]');
    if (stored.length > 0) {
      localStorage.removeItem('pendingErrors');
      for (const storedError of stored) {
        await sendErrorToBackend(storedError, token);
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }

  isProcessingQueue = false;
};

/**
 * Log error to backend
 * @param {Object} errorData - Error information
 */
export const logErrorToBackend = async (errorData) => {
  try {
    const payload = {
      errorMessage: errorData.message || 'Unknown error',
      errorStack: errorData.stack || '',
      componentName: errorData.component || null,
      pageName: errorData.page || getCurrentPage(),
      userAction: errorData.action || null,
      httpStatusCode: errorData.httpStatus || null,
      requestUrl: errorData.url || window.location.href,
      requestMethod: errorData.method || null,
      browserInfo: getBrowserInfo(),
      additionalDetails: {
        ...(errorData.details || {}),
        timestamp: new Date().toISOString(),
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        referrer: document.referrer || null,
        online: navigator.onLine
      }
    };

    const session = await supabase.auth.getSession();
    const token = session?.data?.session?.access_token;

    if (!token) {
      // Queue error for later if not authenticated
      console.warn('\u26a0\ufe0f User not authenticated - queueing error for later');
      pendingErrorQueue.push(payload);
      // Store in localStorage as backup
      try {
        const stored = JSON.parse(localStorage.getItem('pendingErrors') || '[]');
        stored.push({ ...payload, queuedAt: new Date().toISOString() });
        // Keep only last 20 errors to avoid storage issues
        if (stored.length > 20) stored.shift();
        localStorage.setItem('pendingErrors', JSON.stringify(stored));
      } catch (e) {
        // Ignore localStorage errors
      }
      return;
    }

    // Process any queued errors first
    if (pendingErrorQueue.length > 0) {
      processErrorQueue(token);
    }

    await sendErrorToBackend(payload, token);
  } catch (error) {
    console.error('\u274c Error logging failed:', error);
  }
};

/**
 * Get browser information
 * @returns {string} - Browser info string
 */
const getBrowserInfo = () => {
  try {
    const ua = navigator.userAgent;
    const browserMatch = ua.match(/Chrome|Safari|Firefox|Edge|Opera/);
    const osMatch = ua.match(/Windows|Mac|Linux|iPhone|iPad|Android/);

    return JSON.stringify({
      userAgent: ua,
      browser: browserMatch?.[0] || 'Unknown',
      os: osMatch?.[0] || 'Unknown',
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform || 'Unknown'
    });
  } catch (error) {
    return 'Unable to get browser info';
  }
};

/**
 * Setup global error handler
 * Captures unhandled errors and logs them
 */
export const setupGlobalErrorHandler = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    logErrorToBackend({
      message: error?.message || String(error) || 'Unhandled promise rejection',
      stack: error?.stack || '',
      page: getCurrentPage(),
      action: 'Unhandled Promise Rejection',
      details: {
        errorType: 'unhandledrejection',
        promiseReason: String(error)
      }
    });
  });

  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    // Ignore errors from extensions or cross-origin scripts
    if (event.filename && !event.filename.includes(window.location.origin)) {
      return;
    }

    logErrorToBackend({
      message: event.message || event.error?.message || 'Unknown error',
      stack: event.error?.stack || '',
      page: getCurrentPage(),
      action: 'Unhandled JavaScript Error',
      details: {
        errorType: 'globalError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  // Listen for auth state changes to process queued errors
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_IN' && session?.access_token) {
      // Process queued errors after user signs in
      processErrorQueue(session.access_token);
    }
  });

  console.log('\u2705 Global error handler set up');
};

/**
 * Log error with context
 * @param {Error} error - Error object
 * @param {Object} context - Error context
 */
export const captureError = (error, context = {}) => {
  console.error('\u274c Error captured:', error);

  logErrorToBackend({
    message: error?.message || String(error) || 'Unknown error',
    stack: error?.stack || '',
    component: context.component || null,
    page: context.page || getCurrentPage(),
    action: context.action || null,
    httpStatus: context.httpStatus || null,
    url: context.url || window.location.href,
    method: context.method || null,
    details: context.details || {}
  });
};

/**
 * Wrap async function to capture errors
 * @param {Function} fn - Async function to wrap
 * @param {Object} context - Error context
 * @returns {Function} - Wrapped function
 */
export const withErrorHandler = (fn, context = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, {
        ...context,
        action: context.action || 'Async function execution'
      });
      throw error;
    }
  };
};

/**
 * Log API error with context
 * @param {Error} error - Error from API call
 * @param {Object} apiContext - API call context
 */
export const captureAPIError = (error, apiContext = {}) => {
  const errorMessage = error?.message || 'API Error';

  logErrorToBackend({
    message: errorMessage,
    stack: error?.stack || '',
    component: apiContext.component || null,
    page: apiContext.page || getCurrentPage(),
    action: `API ${apiContext.method || 'GET'} ${apiContext.endpoint || 'unknown'}`,
    httpStatus: apiContext.status || null,
    url: apiContext.url || apiContext.endpoint || null,
    method: apiContext.method || null,
    details: {
      endpoint: apiContext.endpoint || apiContext.url,
      method: apiContext.method,
      status: apiContext.status,
      responseBody: apiContext.responseBody,
      requestBody: apiContext.requestBody,
      errorType: 'apiError'
    }
  });
};

/**
 * Create an error logger hook for React components
 * @param {string} componentName - Name of the component
 * @returns {Object} - Error logging utilities for the component
 */
export const useErrorLogger = (componentName) => {
  return {
    captureError: (error, action, details) => {
      captureError(error, {
        component: componentName,
        page: componentName,
        action: action || 'Component error',
        details: details || {}
      });
    },
    captureAPIError: (error, apiContext) => {
      captureAPIError(error, {
        ...apiContext,
        component: componentName,
        page: componentName
      });
    }
  };
};

export default {
  logErrorToBackend,
  setupGlobalErrorHandler,
  captureError,
  withErrorHandler,
  captureAPIError,
  useErrorLogger
};
